import { observer } from "mobx-react";
import { useEffect } from "react";
import { DatePicker, Flex, Form, Input, Modal, Select, Table, Tag } from "antd";
import type { TableColumnsType } from "antd";
import { Calendar2 } from "react-bootstrap-icons";
import { useQueryParams, StringParam, NumberParam, withDefault } from "use-query-params";
import { useNavigate } from "react-router";
import dayjs from "dayjs";
import Utils from "../../../../utils/Utils";
import adminStore from "../../../../stores/admin/adminStore";
import type { IPendingPayment } from "../../../../stores/admin/adminPendingPaymentsStore";
import styles from "./../Clients/index.module.scss";

const AdminPendingPaymentsView = observer(() => {
  const [form] = Form.useForm();
  const nav = useNavigate();

  const [query, setQuery] = useQueryParams({
    perPage: withDefault(NumberParam, 15),
    page: withDefault(NumberParam, 1),
    search: StringParam,
    createdAtFrom: StringParam,
    createdAtTo: StringParam,
    expirationDateFrom: StringParam,
    expirationDateTo: StringParam,
    tariffId: StringParam,
    featureCode: StringParam,
    status: StringParam,
  });

  useEffect(() => {
    if (!adminStore.pendingPayments.lookups?.tariffs?.length) {
      adminStore.pendingPayments.findLookups();
    }
    adminStore.pendingPayments.findAll(query);
    return () => {
      adminStore.pendingPayments.data = undefined;
    };
  }, [query]);

  const applyFilters = (newFilters: Partial<typeof query>) => {
    const filterKeys = [
      "search",
      "createdAtFrom",
      "createdAtTo",
      "expirationDateFrom",
      "expirationDateTo",
      "tariffId",
      "featureCode",
      "status",
    ];

    const isFilterChanged = Object.keys(newFilters).some(
      (key) => filterKeys.includes(key) && newFilters[key as keyof typeof query] !== query[key as keyof typeof query]
    );

    setQuery(
      {
        ...query,
        ...newFilters,
        page: isFilterChanged ? 1 : newFilters.page ?? query.page,
      },
      "replace"
    );
  };

  const handleDelete = async (id: string) => {
    Modal.confirm({
      title: "Удалить запись?",
      okText: "Удалить",
      cancelText: "Отменить",
      onOk: async () => {
        await adminStore.pendingPayments.onDelete(id);
        adminStore.pendingPayments.findAll(query);
      },
    });
  };

  const columns: TableColumnsType<IPendingPayment> = [
    {
      title: "Клиент",
      width: 170,
      render: (_, i) => {
        const fullName = [i.user.profile?.lastName, i.user.profile?.firstName].filter(Boolean).join(" ") || "—";
        return (
          <div
            className={styles.clientName}
            onClick={() => nav(`/clients/${i.user.id}`)} 
          >
            {fullName}
          </div>
        );
      },
    },
    {
      title: "ID транзакции",
      dataIndex: "transactionId",
      width: 160,
      render: (val: string, record: any) => (
        <div>
          {val || "-"}
          {record.isManual && (
            <Tag color="orange" style={{ marginLeft: 8, fontSize: 11 }}>
              Ручной
            </Tag>
      )}
        </div>
      ),
    },
    {
      title: "Email",
      dataIndex: ["user", "email"],
      width: 240,
      ellipsis: true,
    },
    {
      title: "Управление функционалом",
      key: "feature",
      width: 80,
      render: (_, i) => i.featurePrice?.feature?.title || "-",
    },
    {
      title: "Сумма",
      dataIndex: "amount",
      width: 120,
      align: "right" as const,
      render: (val, i) => `${val} ${i.currency || ""}`,
    },
    {
      title: "Статус",
      dataIndex: ["status", "value"],
      width: 180,
      render: (_, i) => (
        <span
          className={`${styles.status} ${styles[i.status?.value || "default"]}`}
        >
          {i.status?.title || "-"}
        </span>
      ),
    },
    {
      title: "Дата платежа",
      dataIndex: "createdAt",
      width: 140,
      align: "center" as const,
      render: (val: string) => Utils.format(val, "DD.MM.YYYY HH:mm"),
    },
    {
      title: "Срок лицензии",
      key: "expiresAt",
      width: 140,
      align: "center" as const,
      render: (_, i) => i.license?.expiresAt ? Utils.format(i.license.expiresAt, "DD.MM.YYYY") : "-",
    },
  ];

  return (
    <div className={styles.area}>
      <Flex style={{ gap: 24, padding: 18 }} className={styles.filters}>
        <Input
          value={query.search ?? ""}
          onChange={(e) => applyFilters({ search: e.target.value, page: 1 })}
          style={{ width: 280 }}
          placeholder="Поиск (ФИО, email, ID транзакции)"
          allowClear
        />

        <Select
          allowClear
          style={{ width: 200 }}
          placeholder="Тариф"
          value={query.tariffId ?? undefined}
          onChange={(val) => applyFilters({ tariffId: val ?? null })}
          options={
            adminStore.pendingPayments.lookups?.tariffs?.map((t) => ({
              label: t.name,
              value: t.id,
            })) || []
          }
        />
        <Select
          allowClear
          style={{ width: 240 }}
          placeholder="Управление функционалом"
          value={query.featureCode ?? undefined}
          onChange={(val) => applyFilters({ featureCode: val ?? null })}
          options={
            adminStore.pendingPayments.lookups?.features?.map((f) => ({
              label: f.title,
              value: f.value,
            })) || []
          }
        />

        <Select
          allowClear
          style={{ width: 160 }}
          placeholder="Статус"
          value={query.status ?? undefined}
          onChange={(val) => applyFilters({ status: val ?? null })}
          options={
            adminStore.pendingPayments.lookups?.paymentsStatuses?.map((s) => ({
              label: s.title,
              value: s.value,
            })) || []
          }
        />

        <DatePicker.RangePicker
          style={{ width: 280 }}
          placeholder={["Создан с", "Создан до"]}
          allowClear
          suffixIcon={<Calendar2 size={20} />}
          value={
            query.createdAtFrom
              ? [dayjs(query.createdAtFrom), dayjs(query.createdAtTo)]
              : null
          }
          format="DD.MM.YYYY"
          onChange={(val) => {
            if (!val) return applyFilters({ createdAtFrom: null, createdAtTo: null });
            if (val?.[0] && val?.[1]) {
              applyFilters({
                createdAtFrom: val[0].startOf("day").toISOString(),
                createdAtTo: val[1].endOf("day").toISOString(),
              });
            }
          }}
        />

        <DatePicker.RangePicker
          style={{ width: 280 }}
          placeholder={["Лицензия с", "Лицензия до"]}
          allowClear
          suffixIcon={<Calendar2 size={20} />}
          value={
            query.expirationDateFrom
              ? [dayjs(query.expirationDateFrom), dayjs(query.expirationDateTo)]
              : null
          }
          format="DD.MM.YYYY"
          onChange={(val) => {
            if (!val)
              return applyFilters({
                expirationDateFrom: null,
                expirationDateTo: null,
              });
            if (val?.[0] && val?.[1]) {
              applyFilters({
                expirationDateFrom: val[0].startOf("day").toISOString(),
                expirationDateTo: val[1].endOf("day").toISOString(),
              });
            }
          }}
        />
      </Flex>

      <Table
        dataSource={adminStore.pendingPayments.data?.data}
        rowKey={(e) => e.id}
        loading={!adminStore.pendingPayments.data}
        scroll={{ y: "calc(100vh - 324px)", x: "max-content" }}
        showHeader={!!adminStore.pendingPayments.data?.data?.length}
        sticky
        columns={columns}
        footer={() => {
          const meta = adminStore.pendingPayments.data?.meta;

          if (!meta?.total || !adminStore.pendingPayments.data?.data?.length) {
            return null;
          }
          if (!meta?.total) return null;

          const from = Math.min((query.page - 1) * query.perPage + 1, meta.total);
          const to = Math.min(query.page * query.perPage, meta.total);

          return (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Показано {from}–{to} из {meta.total}</span>
              {meta.total_amount && (
                <span style={{ fontWeight: 500 }}>
                  Итого: { meta.total_amount } ₽
                </span>
              )}
            </div>
          );
        }}
        pagination={{
          current: query.page,
          pageSize: query.perPage,
          total: adminStore?.pendingPayments.data?.meta?.total,
          showSizeChanger: true,
          pageSizeOptions: ["15", "20", "50"],
        }}
        onChange={({ current, pageSize }) =>
          applyFilters({ page: current, perPage: pageSize })
        }
      />
    </div>
  );
});

export default AdminPendingPaymentsView;
